import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { ClarityService } from '@core/analytics/clarity.service';
import { SessionService } from '@core/services/session.service';
import { I18nService } from '@core/i18n/i18n.service';

/**
 * Traduce cada navegación del sitio público (fridest.com) a tags y eventos de Clarity.
 *
 * Mide el descubrimiento del huésped: home → búsqueda → ficha de hotel → cuenta.
 * (La reserva en sí ocurre dentro del embed de /hotels/:slug y la captura el proyecto
 * Clarity del embed, no este.)
 *
 * Todo se deriva de la URL + sesión: no hay que instrumentar componentes.
 */
@Injectable({ providedIn: 'root' })
export class ClarityTrackingService {
    private readonly router = inject(Router);
    private readonly clarity = inject(ClarityService);
    private readonly session = inject(SessionService);
    private readonly i18n = inject(I18nService);

    private started = false;

    /** Arranca Clarity y engancha el seguimiento al router. Llamar solo en navegador. */
    async start(): Promise<void> {
        if (this.started) return;
        this.started = true;

        // Si Clarity no carga, la app sigue normal: tags/eventos quedan no-op y el
        // seguimiento del router igual se arma (red de seguridad, nunca rompe la página).
        try {
            await this.clarity.init();
        } catch {
            // noop
        }

        this.track(this.router.url);
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(e => this.track(e.urlAfterRedirects));
    }

    private track(url: string): void {
        const path = (url.split('?')[0] || '').split('#')[0];
        const segments = path.split('/').filter(Boolean);
        const first = segments[0] ?? '';

        this.clarity.setTag('language', this.i18n.currentLang);

        if (first === 'auth') {
            this.trackAuth(segments[1] ?? 'signin');
        } else if (first === 'account') {
            this.trackAccount(segments[1] ?? 'bookings');
        } else {
            this.trackPublic(segments);
        }
    }

    private trackPublic(segments: string[]): void {
        this.clarity.setTag('surface', 'public');
        const first = segments[0] ?? '';

        if (first === '') {
            this.clarity.setTag('page', 'home');
            this.clarity.event('home_viewed');
            return;
        }

        if (first === 'hotels') {
            const slug = segments[1] ?? '';
            this.clarity.setTag('page', 'hotel');
            this.clarity.setTag('hotel', slug);
            this.clarity.event('hotel_viewed');
            return;
        }

        // search, contact, privacy, terms, etc.
        this.clarity.setTag('page', first);
        if (first === 'search') this.clarity.event('search_page_viewed');
    }

    private trackAuth(authPage: string): void {
        this.clarity.setTag('surface', 'auth');
        this.clarity.setTag('page', 'auth-' + authPage);
        if (authPage === 'register') this.clarity.event('register_started');
    }

    private trackAccount(accountPage: string): void {
        this.clarity.setTag('surface', 'account');
        this.clarity.setTag('page', 'account-' + accountPage);

        const user = this.session.getUser();
        if (user?.id) this.clarity.identify(user.id);
    }
}
